import { useCallback, useState } from 'react';
import {
  Button,
  Cell,
  Field,
  Finding,
  Label,
  Mono,
  Row,
  Section,
  Status,
  Table,
  type Tone,
} from '../ui.tsx';
import { CaptureError, type IngestReceipt } from '../lib/capture.ts';
import {
  buildFileProvenance,
  ingestFile,
  provenanceComplete,
  type FileIngestProgress,
  type FileProvenanceFields,
} from '../lib/file-ingest.ts';

/**
 * Add evidence by upload — a plain file (or a drag of several), each carrying a
 * declared provenance manifest, ingested through the third-party contract.
 *
 * The grade is **declared, not captured**, and the panel says so before and
 * after commit: a browser upload cannot claim device attestation, so the honest
 * claim is "integrity guaranteed from ingest, provenance as declared by the
 * uploader" and never more (evac-evidence spec §1.5). The provenance the schema
 * requires is collected once and shared across every file in the batch.
 *
 * Batch is per-file, deliberately: each file becomes its own ingest with its
 * own asset and receipt, and a file that fails stays on the list with its error
 * and a retry — a failed upload is never a silently dropped one.
 */

function formatBytes(n: number): string {
  if (n >= 1_073_741_824) return `${(n / 1_073_741_824).toFixed(1)} GB`;
  if (n >= 1_048_576) return `${(n / 1_048_576).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

type ItemStatus =
  | { kind: 'queued' }
  | { kind: 'running'; progress: FileIngestProgress }
  | { kind: 'done'; receipt: IngestReceipt }
  | { kind: 'failed'; message: string; canRetry: boolean };

interface Item {
  id: string;
  file: File;
  status: ItemStatus;
}

let seq = 0;
function nextId(): string {
  seq += 1;
  return `item-${seq}`;
}

function progressLabel(progress: FileIngestProgress): string {
  switch (progress.phase) {
    case 'hashing':
      return 'Hashing';
    case 'grant':
      return 'Authorising';
    case 'opening':
      return 'Opening session';
    case 'uploading':
      return `Uploading part ${Math.min(progress.done + 1, progress.total)} of ${progress.total}`;
    case 'completing':
      return 'Sealing';
    case 'done':
      return 'Committed';
  }
}

function statusTone(status: ItemStatus): Tone {
  if (status.kind === 'done') return 'safe';
  if (status.kind === 'failed') return 'alert';
  if (status.kind === 'running') return 'attention';
  return 'neutral';
}

/** A CaptureError names the hop that broke; turn it into an honest, actionable line. */
function faultOf(err: unknown): { message: string; canRetry: boolean } {
  if (err instanceof CaptureError) {
    if (err.stage === 'reason_required') {
      return {
        message:
          'The evidence service needs a stated reason for access before it will accept this upload. Open the inbox to state one, then try again.',
        canRetry: false,
      };
    }
    return { message: `Upload failed: ${err.message}`, canRetry: true };
  }
  return {
    message: err instanceof Error ? `Upload failed: ${err.message}` : 'Upload failed.',
    canRetry: true,
  };
}

const EMPTY_FIELDS: FileProvenanceFields = {
  sourceDescription: '',
  originalCustodian: '',
  howObtained: '',
  declaredBy: '',
  claimedCapturedAt: '',
};

export function Ingest({
  orgId,
  onClose,
  onCommitted,
}: {
  orgId: string;
  onClose: () => void;
  /** Called after each file commits, so the inbox can refresh. */
  onCommitted: () => void;
}) {
  const [items, setItems] = useState<Item[]>([]);
  const [fields, setFields] = useState<FileProvenanceFields>(EMPTY_FIELDS);
  const [running, setRunning] = useState(false);
  const [dragging, setDragging] = useState(false);

  const setField = (key: keyof FileProvenanceFields) => (value: string) =>
    setFields((f) => ({ ...f, [key]: value }));

  const addFiles = useCallback((fileList: FileList | File[]) => {
    const incoming = Array.from(fileList);
    if (incoming.length === 0) return;
    setItems((prev) => [
      ...prev,
      ...incoming.map((file): Item => ({
        id: nextId(),
        file,
        // A zero-byte file cannot be an asset (a manifest needs a positive
        // byte count); mark it failed up front rather than at ingest, and it
        // stays visible rather than vanishing.
        status:
          file.size === 0
            ? {
                kind: 'failed',
                message: 'This file is empty — nothing to ingest.',
                canRetry: false,
              }
            : { kind: 'queued' },
      })),
    ]);
  }, []);

  const removeItem = (id: string) => setItems((prev) => prev.filter((i) => i.id !== id));

  const update = (id: string, status: ItemStatus) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));

  const runOne = useCallback(
    async (id: string, file: File, provenanceFields: FileProvenanceFields) => {
      const provenance = buildFileProvenance(provenanceFields);
      update(id, { kind: 'running', progress: { phase: 'hashing' } });
      try {
        const receipt = await ingestFile(orgId, file, provenance, (progress) =>
          update(id, { kind: 'running', progress }),
        );
        update(id, { kind: 'done', receipt });
        onCommitted();
      } catch (err) {
        const fault = faultOf(err);
        update(id, { kind: 'failed', ...fault });
      }
    },
    [orgId, onCommitted],
  );

  const runnable = items.filter(
    (i) => i.status.kind === 'queued' || (i.status.kind === 'failed' && i.status.canRetry),
  );
  const complete = provenanceComplete(fields);

  const start = useCallback(async () => {
    if (running || !provenanceComplete(fields)) return;
    const batch = items
      .filter(
        (i) => i.status.kind === 'queued' || (i.status.kind === 'failed' && i.status.canRetry),
      )
      .map((i) => ({ id: i.id, file: i.file }));
    if (batch.length === 0) return;
    setRunning(true);
    for (const { id, file } of batch) {
      await runOne(id, file, fields);
    }
    setRunning(false);
  }, [running, fields, items, runOne]);

  const retryOne = useCallback(
    async (id: string, file: File) => {
      if (running) return;
      setRunning(true);
      await runOne(id, file, fields);
      setRunning(false);
    },
    [running, fields, runOne],
  );

  const committed = items.filter((i) => i.status.kind === 'done').length;

  return (
    <Section
      title="Add evidence"
      action={
        <Button variant="quiet" onClick={onClose}>
          Close
        </Button>
      }
    >
      {/* The honest grade, stated before anything is uploaded. */}
      <Finding tone="neutral">
        An uploaded file is graded <strong className="font-medium">declared</strong>, not captured.
        The bytes are hashed in your browser and the platform guarantees integrity from the moment
        it receives them — a copy can be re-hashed against the record's content hash — but a browser
        cannot attest where a file came from, so the record asserts only "provenance as declared
        below".
      </Finding>

      {/* File chooser + drop zone. The <input> is the accessible control; the
          drag handlers are an enhancement over it. */}
      <label
        className={`mt-4 flex cursor-pointer flex-col items-start gap-2 border border-dashed px-4 py-6 text-[14px] ${
          dragging ? 'border-[var(--focus)] bg-[var(--paper-3)]' : 'border-[var(--line-2)]'
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
        }}
      >
        <span className="font-medium text-[var(--ink)]">Choose files, or drag them here</span>
        <span className="text-[var(--ink-2)]">
          Each file becomes its own evidence record with the provenance below.
        </span>
        <input
          type="file"
          multiple
          aria-label="Choose evidence files to upload"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = '';
          }}
          className="mt-1 text-[13px] text-[var(--ink-2)] file:mr-3 file:rounded-[var(--radius-1)] file:border file:border-[var(--line-2)] file:bg-[var(--paper)] file:px-3 file:py-1.5 file:text-[13px] file:text-[var(--ink)]"
        />
      </label>

      {/* Provenance — the fields the schema requires (spec §1.5), collected once
          and shared by every file in the batch. Locked once ingest starts so a
          committed record and a queued one carry the same declaration. */}
      <div className="mt-5 grid max-w-[640px] gap-4">
        <Label>Provenance — declared for every file above</Label>
        <Field
          label="What is this?"
          value={fields.sourceDescription}
          onChange={setField('sourceDescription')}
          placeholder="CCTV export, front entrance, 14:30–14:45"
          hint="A plain description of the source. Recorded verbatim."
        />
        <Field
          label="Original custodian"
          value={fields.originalCustodian}
          onChange={setField('originalCustodian')}
          placeholder="Who held this before you"
        />
        <Field
          label="How obtained"
          value={fields.howObtained}
          onChange={setField('howObtained')}
          placeholder="Exported to USB by the duty manager and handed to me"
        />
        <Field
          label="Claimed capture time (optional)"
          value={fields.claimedCapturedAt ?? ''}
          onChange={setField('claimedCapturedAt')}
          type="datetime-local"
          hint="When the source says it was recorded — a declaration, not something we can attest."
        />
        <Field
          label="Declared by"
          value={fields.declaredBy}
          onChange={setField('declaredBy')}
          placeholder="Your name — you are making this declaration"
          hint="Recorded as the declaring custodian on every file's provenance."
        />
      </div>

      {items.length > 0 ? (
        <div className="mt-6">
          <Table head={['File', 'Size', 'Grade', 'Status', '']}>
            {items.map((item) => (
              <Row key={item.id}>
                <Cell wide>{item.file.name}</Cell>
                <Cell numeric>{formatBytes(item.file.size)}</Cell>
                <Cell>
                  {item.status.kind === 'failed' && !item.status.canRetry ? '—' : 'Declared'}
                </Cell>
                <Cell>
                  <Status tone={statusTone(item.status)}>
                    {item.status.kind === 'queued'
                      ? 'Queued'
                      : item.status.kind === 'running'
                        ? `${progressLabel(item.status.progress)}…`
                        : item.status.kind === 'done'
                          ? 'Committed'
                          : item.status.message}
                  </Status>
                  {item.status.kind === 'done' ? (
                    <div className="mt-0.5">
                      <Mono>{item.status.receipt.assetId}</Mono>
                    </div>
                  ) : null}
                </Cell>
                <Cell>
                  {item.status.kind === 'failed' && item.status.canRetry ? (
                    <Button
                      variant="quiet"
                      label={`Retry ${item.file.name}`}
                      disabled={running}
                      onClick={() => void retryOne(item.id, item.file)}
                    >
                      Retry
                    </Button>
                  ) : item.status.kind === 'queued' || item.status.kind === 'failed' ? (
                    <Button
                      variant="quiet"
                      label={`Remove ${item.file.name}`}
                      disabled={running}
                      onClick={() => removeItem(item.id)}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Cell>
              </Row>
            ))}
          </Table>
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <Button
          variant="primary"
          disabled={running || runnable.length === 0 || !complete}
          onClick={() => void start()}
        >
          {running
            ? 'Ingesting…'
            : runnable.length > 0
              ? `Ingest ${runnable.length} file${runnable.length === 1 ? '' : 's'}`
              : 'Ingest'}
        </Button>
        {!complete && items.length > 0 ? (
          <span className="text-[14px] text-[var(--ink-2)]">
            Fill in the provenance above — every file is recorded with it.
          </span>
        ) : null}
        {committed > 0 ? (
          <span className="text-[14px] text-[var(--ink-2)]">
            {committed} committed to evidence, each graded declared.
          </span>
        ) : null}
      </div>
    </Section>
  );
}
